import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import sessionService from '../api/sessionService';

export const DEFAULT_EVENT_TYPES = [
  'wedding', 'travel', 'social', 'gathering', 'sport', 'family', 'professional', 'nature', 'food', 'architecture', 'other'
];

/**
 * SessionEventTypeManager
 * Allows selecting multiple user-defined event types for a session.
 * These are the album/event context types set by the user (e.g., wedding, travel) —
 * distinct from features.eventType on each image which is the AI-detected per-image type.
 *
 * Props:
 *   currentSession        – session object with { uniqueId, settings: { eventTypes, eventType } }
 *   refreshCurrentSession – async callback to reload session after save
 *   showSnackbar          – (message, severity) callback
 */
function SessionEventTypeManager({ currentSession, refreshCurrentSession, showSnackbar }) {
  const [customEventTypes, setCustomEventTypes] = useState(() => {
    try {
      const stored = localStorage.getItem('customEventTypes');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const getSessionTypes = (sess) => {
    if (sess?.settings?.eventTypes?.length > 0) return sess.settings.eventTypes;
    if (sess?.settings?.eventType) return [sess.settings.eventType];
    return [];
  };

  const [selectedTypes, setSelectedTypes] = useState(() => getSessionTypes(currentSession));
  const [dropdownValue, setDropdownValue] = useState('');
  const [newEventType, setNewEventType] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    setSelectedTypes(getSessionTypes(currentSession));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession]);

  useEffect(() => {
    try {
      localStorage.setItem('customEventTypes', JSON.stringify(customEventTypes));
    } catch {
      // ignore storage errors
    }
  }, [customEventTypes]);

  const allOptions = [...DEFAULT_EVENT_TYPES, ...customEventTypes];

  const saveTypes = async (types) => {
    setSelectedTypes(types);
    try {
      await sessionService.updateSession(currentSession.uniqueId, {
        settings: { eventTypes: types, eventType: types[0] || '' }
      });
      await refreshCurrentSession();
      showSnackbar('Event types updated', 'success');
    } catch (err) {
      showSnackbar(`Failed to update event types: ${err.message}`, 'error');
    }
  };

  const handleDropdownAdd = async (val) => {
    if (!val || selectedTypes.includes(val)) {
      setDropdownValue('');
      return;
    }
    setDropdownValue('');
    await saveTypes([...selectedTypes, val]);
  };

  const handleRemove = async (type) => {
    await saveTypes(selectedTypes.filter(t => t !== type));
  };

  const handleAddCustom = () => {
    const trimmed = newEventType.trim();
    if (!trimmed) { showSnackbar('Event type name cannot be empty', 'warning'); return; }
    if (allOptions.includes(trimmed)) { showSnackbar('Event type already exists', 'warning'); return; }
    setCustomEventTypes(prev => [...prev, trimmed]);
    setNewEventType('');
    setShowAddDialog(false);
    showSnackbar(`"${trimmed}" added — select it from the dropdown to apply`, 'info');
  };

  const unselectedOptions = allOptions.filter(t => !selectedTypes.includes(t));

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>Session Event Types</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Select one or more types describing this session (e.g. "wedding" + "family"). Used by the AI to tailor image analysis and story generation.
      </Typography>

      {/* Selected types as deletable chips */}
      {selectedTypes.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          {selectedTypes.map(type => (
            <Chip
              key={type}
              label={type}
              onDelete={() => handleRemove(type)}
              color="primary"
              variant="filled"
              size="small"
            />
          ))}
        </Stack>
      )}

      {/* Add from dropdown */}
      <Box display="flex" alignItems="center" gap={1}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Add event type</InputLabel>
          <Select
            value={dropdownValue}
            label="Add event type"
            onChange={e => handleDropdownAdd(e.target.value)}
          >
            {unselectedOptions.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" startIcon={<Add />} onClick={() => setShowAddDialog(true)}>
          Add custom
        </Button>
      </Box>

      {/* Add custom event type dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)}>
        <DialogTitle>Add Custom Event Type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Event type name"
            fullWidth
            value={newEventType}
            onChange={e => setNewEventType(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddCustom()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddCustom} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SessionEventTypeManager;
